/* eslint-disable no-mixed-operators */
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Panel, classnames, Icon } from "@culturehq/components";

import { useProfile } from "app/context";
import { canViewLandingPage, isSuperAdmin } from "app/permissions";
import { isProgressive, Trans } from "app/utils";
import FilterAccordian from "components/common/FilterAccordian";
import styles from "./AdminSidebar.module.scss";

const CultureHQAdmin = ({ externalMappedIds, location }) => (
  <ul className="e_label">
    <li>
      <NavLink to="/admin/chq/organizations" exact activeClassName="active">
        <Icon className="ac_icon" icon={location.pathname.includes("/admin/chq/organizations") ? "building-filled" : "building"} /> <Trans>Organizations</Trans>
      </NavLink>
    </li>
    <li>
      <NavLink to="/admin/chq/users" exact activeClassName="active">
        <Icon className="ac_icon" icon={location.pathname === "/admin/chq/users" ? "play-filled" : "play"} /> <Trans>Simulation</Trans>
      </NavLink>
    </li>
    <li>
      <NavLink to="/admin/chq/slack" exact activeClassName="active">
        <Icon className="ac_icon" icon={location.pathname === "/admin/chq/slack" ? "web-development-filled" : "web-development"} /> <Trans>Slack Users</Trans>
      </NavLink>
    </li>
    <li>
      <NavLink to="/admin/chq/ai-context" exact activeClassName="active">
        <Icon className="ac_icon" icon={location.pathname === "/admin/chq/ai-context" ? "web-development-filled" : "web-development"} /> <Trans>AI Context</Trans>
      </NavLink>
    </li>
    <li>
      <NavLink to="/admin/chq/default-prompts" exact activeClassName="active">
        <Icon className="ac_icon" icon={location.pathname === "/admin/chq/default-prompts" ? "web-development-filled" : "web-development"} /> <Trans>Default Prompts</Trans>
      </NavLink>
    </li>
    <li>
      <NavLink to="/admin/chq/virtual-backgrounds" exact activeClassName="active">
        <Icon className="ac_icon" icon={location.pathname === "/admin/chq/virtual-backgrounds" ? "web-development-filled" : "web-development"} /> <Trans>Virtual Backgrounds</Trans>
      </NavLink>
    </li>
    <li>
      <NavLink to="/admin/chq/privacy-policies" exact activeClassName="active">
        <Icon className="ac_icon" icon={location.pathname === "/admin/chq/privacy-policies" ? "web-development-filled" : "web-development"} /> <Trans>Privacy Policies</Trans>
      </NavLink>
    </li>
    {externalMappedIds && (
      <li>
        <NavLink
          activeClassName="active"
          isActive={(match, location) => match || location.pathname.includes("/admin/mappers")}
          to="/admin/mappers"
        >
          <Icon
            className="ac_icon"
            icon="connections"
          />
          <Trans>External Mappers</Trans>
        </NavLink>
      </li>
    )}
    <li>
      <NavLink to="/admin/chq/organization-users" exact activeClassName="active">
        <Icon className="ac_icon" icon={location.pathname === "/admin/chq/organization-users" ? "web-development-filled" : "web-development"} /> <Trans>Users without Org</Trans>
      </NavLink>
    </li>
    <li>
      <NavLink to="/admin/chq/monitor-ai-usage" exact activeClassName="active">
        <Icon className="ac_icon" icon={location.pathname === "/admin/chq/monitor-ai-usage" ? "web-development-filled" : "web-development"} /> <Trans>Monitor AI Usage</Trans>
      </NavLink>
    </li>
  </ul>
);

const Analytics = ({ culturehqAdmin, location, eventsEnabled }) => (
  <ul className="e_label">
    {eventsEnabled && (
      <li>
        <NavLink to="/admin/analytics" exact activeClassName="active">
          <Icon className="ac_icon" icon={location.pathname === "/admin/analytics" ? "analytics" : "analytics-ol"} /> <Trans>Events</Trans>
        </NavLink>
      </li>
    )}
    <li>
      <NavLink
        to="/admin/analytics/internal"
        isActive={(match, location) => ((match || location.pathname.includes("/admin/analytics/internal")))}
        activeClassName="active"
      >
        <Icon className="ac_icon" icon={location.pathname.includes("/admin/analytics/internal") ? "diagram-filled" : "diagram"} /> <Trans>Internal</Trans>
      </NavLink>
    </li>
    <li>
      <NavLink
        to="/admin/analytics/external"
        isActive={(match, location) => ((match || location.pathname.includes("/admin/analytics/external")))}
        activeClassName="active"
      >
        <Icon className="ac_icon" icon={location.pathname.includes("/admin/analytics/external") ? "global-filled" : "global-outline"} /> <Trans>External</Trans>
      </NavLink>
    </li>
    {culturehqAdmin && (
      <li>
        <NavLink
          to="/admin/analytics/social"
          isActive={(match, location) => ((match || location.pathname.includes("/admin/analytics/social")))}
          activeClassName="active"
        >
          <Icon className="ac_icon" icon={location.pathname.includes("/admin/analytics/social") ? "thumbsup-social" : "linkedin-thumbsup-outlined"} /> <Trans>Social</Trans>
        </NavLink>
      </li>
    )}
  </ul>
);

const Settings = ({ location, isEducational }) => {
  const { profile } = useProfile();
  const { organization } = profile;
  const filteredMappers = organization.externalMappers.filter(mapper => mapper.label);
  const firstLinkId = `${filteredMappers[0]?.label?.toLowerCase()?.replace(/\s+/g, "-")}` || "";

  return (
    <ul className="e_label">
      <li>
        <NavLink to="/admin/users" exact activeClassName="active">
          <Icon className="ac_icon" icon={location.pathname === "/admin/users" ? "ios-people" : "ios-people-outline"} /> <Trans>Users</Trans>
        </NavLink>
      </li>
      <li>
        <NavLink
          activeClassName="active"
          isActive={(match, location) => (match || location.pathname.includes("/admin/companies") || (match || location.pathname.includes("/admin/locations") || location.pathname.includes("/admin/values") || location.pathname.includes("/admin/interests") || location.pathname.includes("/admin/skills") || location.pathname.includes("/admin/programs") || location.pathname.includes("/admin/class-years") || location.pathname.includes("/admin/clubs") || location.pathname.includes("/admin/degrees")))}
          to={isEducational ? "/admin/locations" : "/admin/departments"}
        >
          <Icon
            className="ac_icon"
            icon={location.pathname.includes("/admin/companies") || location.pathname.includes("/admin/departments") || location.pathname.includes("/admin/locations") || location.pathname.includes("/admin/values") || location.pathname.includes("/admin/interests") || location.pathname.includes("/admin/skills") || location.pathname.includes("/admin/programs") || location.pathname.includes("/admin/class-years") || location.pathname.includes("/admin/clubs") || location.pathname.includes("/admin/degrees") ? "supermarket-filled" : "supermarket"}
          />
          <Trans>Tags</Trans>
        </NavLink>
      </li>
      {organization.externalMappedIds && (
        <li>
          <NavLink
            activeClassName="active"
            isActive={(match, location) => match || location.pathname.includes("/admin/mappings/")}
            to={`/admin/mappings/${firstLinkId}`}
          >
            <Icon
              className="ac_icon"
              icon="connections"
            />
            <Trans>Job Mappings</Trans>
          </NavLink>
        </li>
      )}
      {organization.externalMappedIds && filteredMappers.map(mapper => {
        if (!mapper.url) {
          return null;
        }
        return (
          <li key={mapper.id}>
            <NavLink
              activeClassName="active"
              isActive={(match, location) => match || location.pathname === mapper.url}
              to={mapper.url}
            >
              <Icon
                className="ac_icon"
                icon={
                  location.pathname === mapper.url
                    || filteredMappers.some(map => location.pathname === map.url)
                    ? "supermarket-filled"
                    : "supermarket"
                }
              />
              <Trans>{mapper.label}</Trans>
            </NavLink>
          </li>
        );
      })}

      <li>
        <NavLink
          activeClassName="active"
          isActive={(match, location) => ((match || location.pathname.includes("/admin/widgets/disabled")))}
          to="/admin/widgets"
        >
          <Icon
            className="ac_icon"
            icon={location.pathname === "/admin/widgets" || location.pathname === "/admin/widgets/disabled" ? "sticky-note-filled" : "sticky-note"}
          />
          <Trans>Widgets</Trans>
        </NavLink>
      </li>
      <li>
        <NavLink
          activeClassName="active"
          isActive={(match, location) => ((match || location.pathname.includes("/admin/flags")))}
          to="/admin/feedback"
        >
          <Icon
            className="ac_icon"
            icon={location.pathname === "/admin/feedback" || location.pathname.includes("/admin/flags") ? "file-filled" : "file"}
          />
          <Trans>Review</Trans>
        </NavLink>
      </li>
      <li>
        <NavLink
          activeClassName="active"
          isActive={(match, location) => ((match || location.pathname.includes("/admin/microsoft") || location.pathname.includes("/admin/google") || location.pathname.includes("/admin/social")))}
          to="/admin/slack"
        >
          <Icon
            className="ac_icon"
            icon={location.pathname === "/admin/slack" || location.pathname === "/admin/microsoft" || location.pathname === "/admin/google" || location.pathname === "/admin/social" ? "web-development-filled" : "web-development"}
          />
          <Trans>Integrations</Trans>
        </NavLink>
      </li>
    </ul>
  );
};

const CustomFilterTitle = () => (
  <div className={styles.StoriesAccordianTitle}>
    <Icon
      className="ac_icon"
      icon="open-book"
    />
    <span className={styles.StoriesAccordianTitleText}><Trans>Stories</Trans></span>
  </div>
);

export const StoriesMenu = () => {
  const { profile } = useProfile();
  const storyFormsArr = ["story-questions", "story-reviews"];
  const indexStoryForm = storyFormsArr.findIndex(element => window.location.href.includes(element));
  const initialOpen = indexStoryForm >= 0;
  return (
    <FilterAccordian
      title={<CustomFilterTitle />}
      initialOpen={initialOpen}
      className={classnames(styles.StoriesAccordian, "Stories-Accordian")}
    >
      <ul className="f-label">
        <li>
          <NavLink className="sub-menu-item" to="/admin/story-questions" exact activeClassName="active">
            <Trans>Prompts</Trans>
          </NavLink>
        </li>
        <li>
          <NavLink
            className="sub-menu-item"
            to="/admin/story-reviews/pending"
            activeClassName="active"
            isActive={(match, location) => ((match || location.pathname.includes("/admin/story-reviews")))}
          >
            <Trans>Review</Trans>
            {profile.pendingStoryReviews?.length > 0 && <em className="Notification-Badge">{profile.pendingStoryReviews.length}</em>}
          </NavLink>
        </li>
      </ul>
    </FilterAccordian>
  );
};

const Manage = ({ location, profile }) => {
  const { organization: { eventsEnabled, isEducational, recognitionsEnabled } } = profile;

  return (
    <ul className="e_label">
      <StoriesMenu />
      {isSuperAdmin(profile) && canViewLandingPage(profile) && (
        <li>
          <NavLink to="/admin/landing-page" exact activeClassName="active">
            <Icon className="ac_icon" icon={location.pathname === "/admin/landing-page" ? "page-filled" : "page"} /> <Trans>Culture Page</Trans>
          </NavLink>
        </li>
      )}

      {(profile.culturehqAdmin
        || (isSuperAdmin(profile) && isProgressive(profile.organization.name))) && (
        <li>
          <NavLink to="/admin/templates" exact activeClassName="active">
            <Icon className="ac_icon" icon={location.pathname === "/admin/templates" ? "page-filled" : "page"} /> <Trans>Templates</Trans>
          </NavLink>
        </li>
      )}

      {isSuperAdmin(profile) && eventsEnabled && (
        <li>
          <NavLink to="/admin/events" exact activeClassName="active">
            <Icon className="ac_icon" icon={location.pathname.includes("/admin/events") ? "calendar-date-filled" : "calendar-date"} /> <Trans>Events</Trans>
          </NavLink>
        </li>
      )}
      {isSuperAdmin(profile) && !isEducational && recognitionsEnabled && (
        <li>
          <NavLink
            activeClassName="active"
            isActive={(match, location) => ((match || location.pathname.includes("/admin/auto-recognitions") || location.pathname.includes("/admin/pending-recognitions")))}
            to="/admin/recognition-types"
          >
            <Icon
              className="ac_icon"
              icon={location.pathname.includes("/admin/recognition-types") || location.pathname === "/admin/auto-recognitions" || location.pathname.includes("/admin/pending-recognitions") ? "medal-filled" : "medal"}
            />
            <Trans>Recognitions</Trans>
          </NavLink>
        </li>
      )}
    </ul>
  );
};

const AdminSettings = () => {
  const location = useLocation();
  const { profile } = useProfile();
  const {
    organization: { eventsEnabled, isEducational, externalMappedIds },
    culturehqAdmin
  } = profile;

  return (
    <div className={classnames(styles["view-sec"], "view-sec")}>
      <h4 className={styles.displayTitle}>
        <Trans>Admin Dashboard</Trans>
      </h4>
      <hr />
      <h4 className={classnames(styles["form-title"], styles["p-t-0"])}><Trans>MANAGE</Trans></h4>
      <Manage
        location={location}
        profile={profile}
      />
      {isSuperAdmin(profile) && (
        <>
          <hr />
          <h4 className={classnames(styles["form-title"], styles["p-t-0"])}><Trans>ANALYTICS</Trans></h4>
          <Analytics
            culturehqAdmin={culturehqAdmin}
            eventsEnabled={eventsEnabled}
            location={location}
          />
          <hr />
          <h4 className={classnames(styles["form-title"], styles["p-t-0"])}><Trans>SETTINGS</Trans></h4>
          <Settings location={location} isEducational={isEducational} />
        </>
      )}
      {culturehqAdmin && (
        <>
          <hr />
          <h4 className={classnames(styles["form-title"], styles["p-t-0"])}><Trans>CULTUREHQ</Trans></h4>
          <CultureHQAdmin externalMappedIds={externalMappedIds} location={location} />
        </>
      )}
    </div>
  );
};

const AdminSidebar = () => (
  <div className="admin-sidebar">
    <Panel className={classnames(styles["sidebar-fixed"], "sidebar-fixed")}>
      <div className={classnames("col-md-12", "p-0")}>
        <AdminSettings />
      </div>
    </Panel>
  </div>
);

export default AdminSidebar;
